/**
 * aiRecolor — motor de recolorizacao de spritesheets LPC pro Estilista IA.
 *
 * A IA devolve uma paleta ({primary, secondary, accent}) e um padrao opcional
 * ('stripes' | 'dots' | 'gradient' | 'checker'). Aqui a gente carrega o
 * spritesheet base (pixel-art LPC), mapeia a luminancia de cada pixel num
 * gradiente construido com a paleta e aplica o padrao por cima — tudo via
 * canvas, preservando o alpha original.
 */

import { getLpcUrl, getAnimationLayer } from './lpcHelper.js';

/**
 * Animacoes geradas pra cada peca criada no ateliê. Sem elas, a peca ficaria
 * presa na pose de caminhada e sairia do lugar quando o heroi ataca.
 */
export const AI_ANIMATIONS = ['idle', 'walk', 'slash', 'thrust', 'spellcast', 'hurt'];

/**
 * Converte '#RRGGBB' (ou '#RGB') em {r, g, b}.
 */
function hexToRgb(hex) {
  if (!hex) return { r: 128, g: 128, b: 128 };
  var h = String(hex).replace('#', '');
  if (h.length === 3) {
    h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
  }
  var num = parseInt(h, 16);
  if (isNaN(num)) return { r: 128, g: 128, b: 128 };
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
}

/**
 * Mistura linear entre duas cores {r,g,b}. t=0 -> a, t=1 -> b.
 */
function mix(a, b, t) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t
  };
}

/**
 * Amostra o gradiente da paleta na posicao lum (0..1).
 * Stops:
 *   0.00 -> mix(preto, primary, 0.35)   (sombras)
 *   0.50 -> primary                     (tom medio)
 *   0.78 -> secondary                   (luz)
 *   1.00 -> mix(secondary, accent, 0.85) (brilho/highlight)
 */
function sampleGradient(stops, lum) {
  if (lum <= stops[0].pos) return stops[0].color;
  for (var i = 1; i < stops.length; i++) {
    if (lum <= stops[i].pos) {
      var prev = stops[i - 1];
      var t = (lum - prev.pos) / (stops[i].pos - prev.pos);
      return mix(prev.color, stops[i].color, t);
    }
  }
  return stops[stops.length - 1].color;
}

/**
 * Recolore um spritesheet LPC inteiro com a paleta/padrao gerados pela IA.
 *
 * @param {string} baseLayer - path LPC do sprite base (ex: 'torso/clothes/longsleeve/male/walk.png')
 * @param {{primary:string, secondary:string, accent:string}} palette
 * @param {null|'stripes'|'dots'|'gradient'|'checker'} pattern
 * @returns {Promise<string>} dataURL PNG do spritesheet recolorido
 */
export async function recolorSpritesheet(baseLayer, palette, pattern) {
  return new Promise(function (resolve, reject) {
    var url = getLpcUrl(baseLayer);
    if (!url) {
      reject(new Error('Sprite base invalido — nao foi possivel montar a URL do spritesheet.'));
      return;
    }

    var img = new Image();
    img.crossOrigin = 'anonymous';

    img.onerror = function () {
      reject(new Error('Nao foi possivel carregar o sprite base (' + url + '). Verifique sua conexao e tente de novo.'));
    };

    img.onload = function () {
      try {
        var w = img.naturalWidth;
        var h = img.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        var imageData;
        try {
          imageData = ctx.getImageData(0, 0, w, h);
        } catch (e) {
          reject(new Error('Nao foi possivel carregar o sprite (CORS)'));
          return;
        }
        var data = imageData.data;

        var primary = hexToRgb(palette && palette.primary);
        var secondary = hexToRgb(palette && palette.secondary);
        var accent = hexToRgb(palette && palette.accent);
        var black = { r: 0, g: 0, b: 0 };

        var stops = [
          { pos: 0.0, color: mix(black, primary, 0.35) },
          { pos: 0.5, color: primary },
          { pos: 0.78, color: secondary },
          { pos: 1.0, color: mix(secondary, accent, 0.85) }
        ];

        for (var y = 0; y < h; y++) {
          for (var x = 0; x < w; x++) {
            var i = (y * w + x) * 4;
            var alpha = data[i + 3];
            if (alpha === 0) continue;

            var r = data[i];
            var g = data[i + 1];
            var b = data[i + 2];

            // 1) Recolorizacao por luminancia
            var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            var c = sampleGradient(stops, lum);

            // 2) Padrao (aplicado depois da recolorizacao)
            if (pattern === 'stripes') {
              if (Math.floor(x / 3) % 2 === 1) c = mix(c, secondary, 0.45);
            } else if (pattern === 'dots') {
              if (x % 7 === 3 && y % 7 === 3) c = mix(c, accent, 0.8);
            } else if (pattern === 'checker') {
              if ((Math.floor(x / 4) + Math.floor(y / 4)) % 2 === 0) c = mix(c, secondary, 0.3);
            } else if (pattern === 'gradient') {
              var t = (y % 64) / 64; // gradiente vertical por frame (frames LPC tem 64px)
              c = mix(c, secondary, t * 0.5);
            }

            data[i] = Math.round(Math.max(0, Math.min(255, c.r)));
            data[i + 1] = Math.round(Math.max(0, Math.min(255, c.g)));
            data[i + 2] = Math.round(Math.max(0, Math.min(255, c.b)));
            // alpha original preservado (data[i + 3] intocado)
          }
        }

        ctx.putImageData(imageData, 0, 0);

        var dataUrl;
        try {
          dataUrl = canvas.toDataURL('image/png');
        } catch (e) {
          reject(new Error('Nao foi possivel carregar o sprite (CORS)'));
          return;
        }
        resolve(dataUrl);
      } catch (e) {
        reject(new Error('Falha ao recolorir o sprite: ' + (e.message || e)));
      }
    };

    img.src = url;
  });
}

/**
 * Recoloriza a peca em TODAS as animacoes do personagem.
 *
 * O corpo troca de spritesheet ao atacar/andar; se a peca so tivesse o
 * 'walk', ela ficaria na pose parada e sairia do lugar durante o ataque.
 * Anima cuja arte-base nao existir no pack e simplesmente pulada.
 *
 * @returns {Promise<{images: {anim: dataURL}, falhas: string[]}>}
 */
export async function recolorAllAnimations(baseLayer, palette, pattern, onProgress) {
  var images = {};
  var falhas = [];

  for (var i = 0; i < AI_ANIMATIONS.length; i++) {
    var anim = AI_ANIMATIONS[i];
    var layer = getAnimationLayer(baseLayer, anim);
    try {
      images[anim] = await recolorSpritesheet(layer, palette, pattern);
    } catch (e) {
      falhas.push(anim);
    }
    if (onProgress) onProgress(anim, i + 1, AI_ANIMATIONS.length);
  }

  return { images: images, falhas: falhas };
}

export default { recolorSpritesheet, recolorAllAnimations, AI_ANIMATIONS };
